import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LOAD_MASTERS } from '@common/master/master';
import { CheckinSearchResultStoreService, ServicingMybookingTransitionService } from '@common/services';
import { CheckinSearchService } from '@common/services/checkin-search/checkin-search.service';
import { environment } from '@env/environment';
import { AswMasterService, CommonLibService } from '@lib/services';
import { Observable, firstValueFrom, lastValueFrom, pipe, take } from 'rxjs';
import { PostJourneysSearchJourneyRequest } from 'src/sdk_checkin';

@Injectable({
  providedIn: 'root',
})
export class CheckinSearchGuard implements CanActivate {
  constructor(
    private router: Router,
    private commonService: CommonLibService,
    private checkinSearchService: CheckinSearchService,
    private checkinSearchResultStoreService: CheckinSearchResultStoreService,
    private aswMasterService: AswMasterService,
    private servicingMybookingTransitionService: ServicingMybookingTransitionService
  ) {
    // チェックイン検索失敗時のエラー文言を表示するため機能不可事由マスタの取得
    this.aswMasterService
      .load([LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON, LOAD_MASTERS.OFFICE_BY_OFFICE_CODE], true)
      .pipe(take(1))
      .subscribe();
  }

  /**
   * チェックイン検索画面表示前の処理
   * @returns
   */
  async checkinSearchGuard() {
    // ASW_TOPからの遷移の場合の処理を行う
    if (this.checkinSearchService.isInflowFromAswTop()) {
      // orderまたeTicketの必須パラメータが存在する場合
      const param = this.checkinSearchService.getPostParameterForAswTop();
      if (param) {
        return await this.runCommonProcess(param, true);
      }

      // ログイン状態の取得
      const aswContextData = this.commonService.aswContextStoreService.aswContextData;
      const loginStatus = aswContextData.loginStatus;

      // リアルログインの場合
      if (this.checkinSearchService.isRealLogin(loginStatus)) {
        const amcMember = this.commonService.amcMemberStoreService.amcMemberData;
        const checkinSearchCondition = this.checkinSearchService.getInitialParameterForRealLogin(amcMember);
        return await this.runCommonProcess(checkinSearchCondition, true);
      }

      // 仮ログインの場合
      if (this.checkinSearchService.isTemporaryLogin(loginStatus)) {
        // 仮ログイン時の画面表示内容の変更、及びログインモーダルの表示はcomponent側で行う。
        return true;
      }
    }

    // 予約詳細からの遷移の場合の処理を行う
    if (this.checkinSearchService.isInflowFromReservation()) {
      const param = this.checkinSearchService.getPostParameterForReservation();
      // パラメータを取得してもundefinedの場合は外部流入は行われていないので、チェックイン検索画面に通す
      if (!!param) {
        // エラー監視及びエラー時の処理
        const subscription = this.checkinSearchResultStoreService.getErrorMessageId$().subscribe((id) => {
          const form = document.createElement('form');
          form.action = this.servicingMybookingTransitionService.getServicingPathWithConnectionKind();
          form.method = 'POST';
          document.body.append(form);
          form.addEventListener('formdata', (event) => {
            const formData = event.formData;
            formData.set('searchType', param.searchType);
            if (param.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.Order) {
              formData.set('orderId', param.orderId!);
            }
            if (param.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket) {
              formData.set('eTicketNumber', param.eTicketNumber!);
            }
            formData.set('firstName', param.firstName);
            formData.set('lastName', param.lastName);
            formData.set('SITE_ID', 'ASW_APP');
            if (!!id) {
              formData.set('errorId', id);
            }
          });
          this.checkinSearchService.clearCheckinSearchStore();
          form.submit();
        });

        const result = await this.runCommonProcess(param, false);
        subscription.unsubscribe();
        return result;
      }
    }

    return true;
  }

  /**
   * 検索条件をチェックイン検索共通処理に流し、遷移先を返す。
   * @param condition チェックイン検索APIのリクエストパラメータ
   * @param isTransitionOnError チェックイン検索APIでエラーが発生した際にチェックイン検索画面に遷移させるかどうか
   * @returns
   */
  async runCommonProcess(
    condition: PostJourneysSearchJourneyRequest,
    isTransitionOnError: boolean
  ): Promise<boolean | UrlTree> {
    const processResult = await lastValueFrom(this.checkinSearchService.checkinSearchCommonProcessOnGuard$(condition));

    // booleanが帰ってくる場合にはエラーが発生しているので
    // 呼び出し元から指定された、チェックイン検索画面に流入しても良いかのフラグを返す
    if (typeof processResult === 'boolean') {
      return isTransitionOnError;
    }
    // Observableが返却された場合には、CPR情報取得APIの結果によってチェックイン対象選択に進むかチェックイン状況詳細に進むか決まる
    // もしURLが存在しなかった場合にはチェックイン検索画面に進む
    if (processResult instanceof Observable) {
      const url = await firstValueFrom(processResult);
      return !!url ? this.router.parseUrl(url) : true;
    } else {
      // それ以外の場合には、AbleDに遷移するためのURL
      if (!!processResult) {
        (window as any).location.href = processResult;
        return false;
      }
      return true;
    }
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    routerState: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // canActiveが呼ばれるたびにチェックイン検索に関連するstoreを初期化する
    this.checkinSearchService.clearCheckinSearchStore();
    return this.checkinSearchGuard();
  }
}
